import React, { createRef, useRef, useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import ActiveFiltersIndicator from "../ActiveFiltersIndicator";
import GroupVisibilityToggle from "../../GroupVisibilityToggle";
import ResetFiltersButton from "../../ResetFiltersButton";
import DealsCarousel from "../../DealsCarousel";
import PriceDigest from "../PriceDigest";
import Login from "../../Login";

const ComparisonPageFrame = ({
  apiError,
  errorTitle = "Something went wrong loading listings.",
  errorSubtitle = "Retrying automatically...",
  onRetry,
  renderFilters,
  groupVisibility,
  setAllGroupsVisibility,
  onResetFilters,
  aboutInitiallyVisible = true,
  aboutContent,
  filtered,
  headingFiltered,
  descriptionFiltered,
  headingUnfiltered,
  descriptionUnfiltered,
  headingRef,
  activeFiltersProps,
  dealsAdapter = null,
  dealsIsDbQuerying = false,
  priceDigestConfig = null,
  personalListEmptyState = null,
  reefWizardEmptyState = null,
  groups,
  groupKey,
  renderGroup,
  groupsClassName = "",
  getGroupClassName = null,
  loading,
  emptyText,
  maxResults = 100,
  overflowContent = null,
}) => {
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [aboutSectionVisible, setAboutSectionVisible] = useState(
    () => aboutInitiallyVisible && (typeof window === "undefined" || window.innerWidth > 768)
  );
  const loadingTextRef = useRef(null);
  const nodeRefs = useRef({});

  const getNodeRef = (key) => {
    if (!nodeRefs.current[key]) {
      nodeRefs.current[key] = createRef();
    }
    return nodeRefs.current[key];
  };

  const renderActiveFilters = () => (
    <ActiveFiltersIndicator {...activeFiltersProps} />
  );

  const renderPersonalListEmptyState = () => {
    if (!personalListEmptyState?.active || groups.length !== 0) return null;

    return (
      <div className="mb-3 p-2 bg-yellow-100 border border-yellow-300 rounded-lg flex flex-col items-center justify-between transition-colors gap-2">
        {personalListEmptyState.isAuthenticated ? (
          <span>{personalListEmptyState.authenticatedMessage}</span>
        ) : (
          <>
            <span>{personalListEmptyState.unauthenticatedMessage}</span>
            <Login />
          </>
        )}
      </div>
    );
  };

  const renderReefWizardEmptyState = () => {
    if (!reefWizardEmptyState?.active || groups.length !== 0) return null;

    const {
      dataType,
      isAuthenticated,
      hasRecommendations,
    } = reefWizardEmptyState;

    return (
      <div className="mb-3 p-2 bg-blue-100 border border-blue-300 rounded-lg flex flex-col items-center justify-between transition-colors gap-2">
        {isAuthenticated ? (
          !hasRecommendations ? (
            <span>
              You haven't received any recommendations from Reef Wizard yet.{" "}
              <a href="/reef-wizard" className="text-blue-600 underline hover:text-blue-800">
                Try Reef Wizard
              </a>{" "}
              to get personalized species recommendations for your tank!
            </span>
          ) : (
            <span>
              No {dataType} matching your Reef Wizard recommendations are currently available.
              Try adjusting other filters or check back later!
            </span>
          )
        ) : (
          <>
            <span>
              Please log in to use Reef Wizard recommendations!{" "}
              <a href="/reef-wizard" className="text-blue-600 underline hover:text-blue-800">
                Learn more about Reef Wizard
              </a>
            </span>
            <Login />
          </>
        )}
      </div>
    );
  };

  if (apiError) {
    return (
      <div className="text-center py-8 bg-white rounded-lg shadow-md p-4">
        <p className="text-gray-600 mb-1">{errorTitle}</p>
        <p className="text-gray-400 text-sm mb-3">{errorSubtitle}</p>
        <button
          onClick={onRetry}
          className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-4 py-1.5 rounded-lg transition-colors"
        >
          Retry now
        </button>
      </div>
    );
  }

  return (
    <>
      {dealsAdapter && (
        <DealsCarousel
          adapter={dealsAdapter}
          isDbQuerying={dealsIsDbQuerying}
        />
      )}
      {priceDigestConfig?.show && (
        <PriceDigest
          dataType={priceDigestConfig.dataType}
          digestData={priceDigestConfig.digestData}
          onFilterApply={priceDigestConfig.onFilterApply}
        />
      )}

      <div className="flex flex-row min-w-0">
        <div className="flex-2 filter-sidebar mr-3 p-3 bg-white shadow rounded-lg">
          <div className="sticky top-0 pt-1">
            <div className="flex flex-row flex-nowrap justify-between items-center gap-2 mb-3">
              <h2 className="text-lg font-bold text-blue-800">Filters</h2>
              <ResetFiltersButton onReset={onResetFilters} />
              <GroupVisibilityToggle
                groupVisibility={groupVisibility}
                setAllGroupsVisibility={setAllGroupsVisibility}
              />
            </div>
            {renderFilters()}
          </div>
        </div>

        <div className="flex-6 min-w-0 w-full">
          <div className="comparison-about mb-4 p-3 bg-white shadow rounded-lg">
            <h2>
              <button
                type="button"
                className="flex justify-between items-center cursor-pointer w-full text-left"
                aria-expanded={aboutSectionVisible}
                onClick={() => setAboutSectionVisible((visible) => !visible)}
              >
                <span className="text-lg font-bold text-blue-800">About</span>
                <span className="text-blue-800" aria-hidden="true">
                  {aboutSectionVisible ? "▲" : "▼"}
                </span>
              </button>
            </h2>

            {aboutSectionVisible && aboutContent}
          </div>

          <div className="comparison-catch p-3 bg-white shadow rounded-t-lg" ref={headingRef}>
            {filtered ? (
              <div>
                <b className="text-green-600 text-lg">{headingFiltered}</b>
                <p>{descriptionFiltered}</p>
              </div>
            ) : (
              <div>
                <b className="text-green-600 text-lg">{headingUnfiltered}</b>
                <p>{descriptionUnfiltered}</p>
              </div>
            )}
          </div>

          <div
            className="filter-bar sticky top-0 bg-white z-10 shadow-md p-3 rounded-b-lg mb-4"
            onClick={() => setFiltersVisible(!filtersVisible)}
          >
            <div className="flex flex-col flex-nowrap justify-between items-center gap-2">
              <div className="flex flex-row flex-nowrap justify-between items-center gap-2 w-full">
                <span className="text-lg font-bold text-blue-800">Filters</span>
                <span className="text-blue-800">
                  {filtersVisible ? "▲" : "▼"}
                </span>
              </div>
              <div className="w-full">{renderActiveFilters()}</div>
            </div>
            <div onClick={(event) => event.stopPropagation()}>
              {filtersVisible && renderFilters()}
            </div>
          </div>

          <div className="filter-sidebar mb-3 sticky top-0 z-10 shadow-md">
            {renderActiveFilters()}
          </div>

          {renderPersonalListEmptyState()}
          {renderReefWizardEmptyState()}

          <TransitionGroup className={groupsClassName || undefined}>
            {groups.length === 0 ? (
              <CSSTransition
                timeout={500}
                classNames="loading-text"
                unmountOnExit
                nodeRef={loadingTextRef}
              >
                <div
                  ref={loadingTextRef}
                  className="comparison-groups-empty text-gray-500 p-3 bg-white shadow rounded-lg"
                >
                  {loading ? "Loading..." : emptyText}
                </div>
              </CSSTransition>
            ) : (
              <>
                {groups.slice(0, maxResults).map((group) => {
                  const key = groupKey(group);
                  const nodeRef = getNodeRef(key);
                  return (
                    <CSSTransition
                      key={key}
                      timeout={500}
                      classNames="fish-item"
                      nodeRef={nodeRef}
                    >
                      <div
                        ref={nodeRef}
                        className={getGroupClassName?.(group) || undefined}
                      >
                        {renderGroup(group)}
                      </div>
                    </CSSTransition>
                  );
                })}
                {groups.length >= maxResults && (
                  <div className="comparison-groups-overflow">
                    {overflowContent}
                  </div>
                )}
              </>
            )}
          </TransitionGroup>
        </div>
      </div>
    </>
  );
};

export default ComparisonPageFrame;
